import * as Sentry from '@sentry/angular';

import { Environment, isProd } from '@environment';
import { AppGitVersion } from '@usheru-hff/shared/utils';

const isBrowserRuntime = () => typeof window !== 'undefined';
const isNotLocalhost = () => !isBrowserRuntime() || !window.location.hostname.includes('localhost');

type InAppBrowserVendor =
  | 'facebook'
  | 'instagram'
  | 'tiktok'
  | 'line'
  | 'miui'
  | 'android-webview'
  | 'ios-webview'
  | 'unknown';

function detectInAppBrowserVendor(ua: string): InAppBrowserVendor | null {
  if (/TikTok|musical_ly(?:_[\d.]+)?|ByteLocale|BytedanceWebview|aweme|trill/i.test(ua)) return 'tiktok';
  if (/FBAN|FBAV|FB_IAB|FBIOS|FB4A|Facebook/i.test(ua)) return 'facebook';
  if (/Instagram/i.test(ua)) return 'instagram';
  if (/Line\//i.test(ua)) return 'line';
  if (/MiuiBrowser/i.test(ua)) return 'miui';
  if (/;\s*wv\)/i.test(ua) || /\bwv\b/i.test(ua)) return 'android-webview';
  if (/WKWebView/i.test(ua)) return /Android/i.test(ua) ? 'android-webview' : 'ios-webview';
  return null;
}

function getTopException(event: any): any {
  return event?.exception?.values?.[0];
}

function getTopFrame(event: any): any {
  return getTopException(event)?.stacktrace?.frames?.at?.(-1);
}

function getUserAgentFromEvent(event: any): string {
  const requestUa =
    event?.request?.headers?.['user-agent'] ??
    event?.request?.headers?.['User-Agent'] ??
    event?.request?.headers?.['USER-AGENT'] ??
    '';
  const navigatorUa = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
  return requestUa || navigatorUa;
}

function getTrackingUuid(): string | null {
  if (typeof sessionStorage === 'undefined') {
    return null;
  }

  try {
    const raw = sessionStorage.getItem('usheru_session');
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Record<string, unknown>;
    return typeof parsed.uuid === 'string' && parsed.uuid.trim() ? parsed.uuid : null;
  } catch {
    return null;
  }
}

function hasDynamicRouteSegment(path: string): boolean {
  return /(^|\/):[A-Za-z0-9_]+(?=\/|$)/.test(path);
}

function toPathname(url: string): string | null {
  const locationOrigin =
    typeof window !== 'undefined' && typeof window.location?.origin === 'string' ? window.location.origin : undefined;

  try {
    const parsed = locationOrigin ? new URL(url, locationOrigin) : new URL(url);
    return parsed.pathname || null;
  } catch {
    return null;
  }
}

function getConcretePathname(event: any): string | null {
  const requestUrl = typeof event?.request?.url === 'string' ? event.request.url : '';
  const pathFromRequest = requestUrl ? toPathname(requestUrl) : null;
  if (pathFromRequest) {
    return pathFromRequest;
  }

  if (typeof window !== 'undefined' && typeof window.location?.pathname === 'string') {
    return window.location.pathname;
  }

  return null;
}

function normalizeRouteContext(event: any): any {
  const concretePath = getConcretePathname(event);
  if (!concretePath) {
    return event;
  }

  const currentTransaction = typeof event?.transaction === 'string' ? event.transaction : '';
  if (!currentTransaction || hasDynamicRouteSegment(currentTransaction)) {
    event.transaction = concretePath;
  }

  const currentCulprit = typeof event?.culprit === 'string' ? event.culprit : '';
  if (!currentCulprit || hasDynamicRouteSegment(currentCulprit)) {
    event.culprit = concretePath;
  }

  event.extra = {
    ...(event.extra ?? {}),
    route_pathname: concretePath
  };

  return event;
}

function buildBrowserDiagnostics(event: any) {
  const ua = getUserAgentFromEvent(event);
  const referrer = typeof document !== 'undefined' ? document.referrer || '' : '';
  const visibility = typeof document !== 'undefined' ? document.visibilityState || '' : '';
  const vendor = detectInAppBrowserVendor(ua);
  const topFrame = getTopFrame(event);
  const topException = getTopException(event);
  const topMessage = topException?.value ?? event?.message ?? '';

  const breadcrumbHints =
    event?.breadcrumbs
      ?.slice?.(-20)
      ?.filter?.((b: any) => {
        const msg = `${b?.message ?? ''} ${JSON.stringify(b?.data ?? {})}`;
        return /PageTranslationRequiredListener|hxp-chat-suppression|IABTranslationJS|m\.facebook\.com/i.test(msg);
      })
      ?.map?.((b: any) => b?.message)
      ?.slice?.(0, 5) ?? [];

  const isPostMessageInvalidAccess =
    topException?.type === 'InvalidAccessError' &&
    /object does not support the operation or argument/i.test(topMessage) &&
    /postMessage/i.test(topFrame?.function ?? '');

  const isViewTransitionHidden =
    topException?.type === 'InvalidStateError' &&
    /View transition was skipped because document visibility state is hidden\./i.test(topMessage);

  const isWebkitMessageHandlerMissing =
    /window\.webkit\.messageHandlers(?:[^']*postMessage)?/i.test(topMessage) ||
    /undefined is not an object \(evaluating 'window\.webkit\.messageHandlers.*postMessage'\)/i.test(topMessage);
  const isAndroidBridgeObjectGone =
    /java object is gone/i.test(topMessage) ||
    /error invoking .*keyboardlogging/i.test(topMessage) ||
    /enableDidUserTypeOnKeyboardLogging/i.test(topMessage);

  return {
    ua,
    referrer,
    visibility,
    inAppBrowserVendor: vendor,
    topFrame: {
      fn: topFrame?.function ?? '',
      file: topFrame?.filename ?? '',
      line: topFrame?.lineno ?? null,
      col: topFrame?.colno ?? null
    },
    topExceptionType: topException?.type ?? '',
    topExceptionMessage: topMessage,
    trackingUuid: getTrackingUuid(),
    breadcrumbHints,
    isPostMessageInvalidAccess,
    isViewTransitionHidden,
    isWebkitMessageHandlerMissing,
    isAndroidBridgeObjectGone
  };
}

function enrichEventWithDiagnostics(event: any): any {
  const diagnostics = buildBrowserDiagnostics(event);
  const tags = { ...(event.tags ?? {}) };

  tags.in_app_browser = diagnostics.inAppBrowserVendor ? 'true' : 'false';
  if (diagnostics.inAppBrowserVendor) {
    tags.in_app_browser_vendor = diagnostics.inAppBrowserVendor;
  }
  if (diagnostics.isPostMessageInvalidAccess) {
    tags.error_family = 'postmessage_invalidaccess';
    event.fingerprint = ['postmessage-invalidaccess', diagnostics.inAppBrowserVendor ?? 'unknown'];
  } else if (diagnostics.isViewTransitionHidden) {
    tags.error_family = 'viewtransition_hidden_document';
    event.fingerprint = ['viewtransition-hidden-document', diagnostics.inAppBrowserVendor ?? 'unknown'];
  } else if (diagnostics.isWebkitMessageHandlerMissing) {
    tags.error_family = 'iab_webkit_messagehandler_missing';
    event.fingerprint = ['iab-webkit-messagehandler-missing', diagnostics.inAppBrowserVendor ?? 'unknown'];
  } else if (diagnostics.isAndroidBridgeObjectGone) {
    tags.error_family = 'iab_android_bridge_object_gone';
    event.fingerprint = ['iab-android-bridge-object-gone', diagnostics.inAppBrowserVendor ?? 'unknown'];
  }

  event.tags = tags;
  event.extra = {
    ...(event.extra ?? {}),
    browser_diagnostics: diagnostics
  };

  return event;
}

export const sentryInit = (
  environment: Environment,
  dsn: string,
  gitVersion: AppGitVersion,
  sampleRate: number = 0.2,
  options: { enableBrowserDiagnostics?: boolean } = {}
): void => {
  if (isProd(environment) && isNotLocalhost()) {
    const nonTrackingErrorSampleRate = Math.min(1, Math.max(0, sampleRate));

    Sentry.init({
      dsn,
      sendDefaultPii: true,
      attachStacktrace: true,
      normalizeDepth: 5,
      integrations: [Sentry.browserTracingIntegration()],
      release: gitVersion.number,
      environment: environment.mode,
      // Keep error events in SDK and apply deterministic sampling in beforeSend.
      sampleRate: 1.0,
      beforeSend(event) {
        const withRoute = normalizeRouteContext(event);
        const diagnostics = buildBrowserDiagnostics(withRoute);
        const withDiagnostics = enrichEventWithDiagnostics(withRoute);
        const topException = getTopException(withDiagnostics);
        const topExceptionType = topException?.type ?? '';
        const topExceptionMessage = String(topException?.value ?? withDiagnostics?.message ?? '');
        withDiagnostics.extra = {
          ...(withDiagnostics.extra ?? {}),
          // Session propagated by tracking-sdk and helpful to correlate failures.
          trackingUuid: diagnostics.trackingUuid
        };

        const iabVendor = diagnostics.inAppBrowserVendor;
        const isKnownIabNoise =
          diagnostics.isWebkitMessageHandlerMissing &&
          (iabVendor === 'facebook' || iabVendor === 'instagram' || iabVendor === 'ios-webview');
        const isKnownAndroidBridgeNoise =
          diagnostics.isAndroidBridgeObjectGone &&
          (iabVendor === 'facebook' || iabVendor === 'instagram' || iabVendor === 'android-webview');
        if (isKnownIabNoise || isKnownAndroidBridgeNoise) {
          return null;
        }

        const isChunkError =
          withDiagnostics.tags?.error_type === 'ChunkLoadError' ||
          withDiagnostics.tags?.error_kind === 'chunk-load' ||
          /failed to fetch dynamically imported module|loading chunk|chunkloaderror|importing a module script failed/i.test(
            topExceptionMessage
          );
        const isTransitionOrIabDomException =
          topExceptionType === 'InvalidAccessError' || topExceptionType === 'InvalidStateError';

        // Always keep critical client-side failures to ease incident debugging.
        if (isChunkError || isTransitionOrIabDomException) {
          return withDiagnostics;
        }

        return Math.random() < nonTrackingErrorSampleRate ? withDiagnostics : null;
      },
      beforeSendTransaction(event) {
        return normalizeRouteContext(event);
      },
      tracesSampler: samplingContext => {
        //  % of performance transactions sent.
        if (samplingContext.attributes['sentry.origin'].includes('pageload')) {
          return 0.5;
        }
        return 0.1;
      }
    });
  }
};

export const sentryInitServer = (
  environment: Environment,
  dsn: string,
  gitVersion: AppGitVersion,
  sampleRate: number = 1.0
): void => {
  if (isProd(environment)) {
    const serverErrorSampleRate = Math.min(1, Math.max(0, sampleRate));

    Sentry.init({
      dsn,
      sendDefaultPii: true,
      attachStacktrace: true,
      normalizeDepth: 5,
      defaultIntegrations: false,
      integrations: [],
      release: gitVersion.number,
      environment: environment.mode,
      sampleRate: 1.0,
      beforeSend(event) {
        const withRoute = normalizeRouteContext(event);
        withRoute.tags = {
          ...(withRoute.tags ?? {}),
          runtime: 'ssr'
        };

        return Math.random() < serverErrorSampleRate ? withRoute : null;
      }
    });
  }
};
